import axiosInstance from "../../../utils/axiosInstance";
import { logger } from "../../../utils/logger";

/**
 * Fetch all users from API
 */
export const fetchUsers = async (token, user, setUsers, setFeedback) => {
  try {
    logger.info("Fetching users...", {
      username: user?.username,
      role: user?.role,
    });
    const res = await axiosInstance.get("/api/user", {
      headers: { Authorization: `Bearer ${token}` },
    });
    setUsers(Array.isArray(res.data) ? res.data : []);
    logger.info("Users fetched successfully", {
      count: res.data.length,
      username: user?.username,
      role: user?.role,
    });
  } catch (err) {
    logger.error("Error fetching users", {
      error: err.message,
      username: user?.username,
      role: user?.role,
    });
    setFeedback({
      open: true,
      message: "Error fetching users",
      severity: "error",
    });
  }
};

/**
 * Handle edit button click
 */
export const handleEditClick = (data, user, setEditUser, setEditModalOpen) => {
  logger.info("User modal Opened for Edit User", {
    userId: data._id,
    user: data.username,
    editedby: user?.username,
    role: user?.role,
  });
  setEditUser(data);
  setEditModalOpen(true);
};

/**
 * Handle edit modal close
 */
export const handleEditModalClose = (
  editModalOpen,
  editUser,
  user,
  setEditModalOpen
) => {
  if (editModalOpen) {
    logger.info("Edit User Modal closed without saving", {
      userId: editUser?._id,
      editedUser: editUser?.username,
      closedBy: user?.username,
      role: user?.role,
    });
  }
  setEditModalOpen(false);
};

/**
 * Handle edit save
 */
export const handleEditSave = async (
  userData,
  token,
  user,
  setUsers,
  setEditModalOpen,
  setFeedback
) => {
  try {
    await axiosInstance.put(
      `/api/user/${userData._id}`,
      {
        username: userData.username,
        password: userData.password,
        role: userData.role,
        email: userData.email,
      },
      { headers: { Authorization: `Bearer ${token}` } }
    );

    // Optimistic update
    setUsers((prevUsers) =>
      prevUsers.map((u) =>
        u._id === userData._id
          ? {
              ...u,
              username: userData.username,
              email: userData.email,
              role: userData.role,
            }
          : u
      )
    );

    setEditModalOpen(false);
    setFeedback({
      open: true,
      message: "User updated successfully",
      severity: "success",
    });
    logger.info("User updated successfully", {
      id: userData._id,
      user: userData.username,
      updatedby: user?.username,
      role: user?.role,
    });
  } catch (err) {
    logger.error("Error updating user", {
      error: err,
      username: user?.username,
      role: user?.role,
    });
    setFeedback({
      open: true,
      message: "Error updating user",
      severity: "error",
    });
  }
};

/**
 * Handle delete button click
 */
export const handleDeleteClick = (
  data,
  userId,
  user,
  setOpenAlert,
  setUserToDelete,
  setDeleteDialogOpen
) => {
  if (data._id === userId) {
    logger.warn("Attempted self-delete", {
      id: data._id,
      user: data.username,
      by: user?.username,
      role: user?.role,
    });
    setOpenAlert(true);
  } else {
    logger.info("User Delete Confirmation opened", {
      id: data._id,
      user: data.username,
      by: user?.username,
      role: user?.role,
    });
    setUserToDelete(data);
    setDeleteDialogOpen(true);
  }
};

/**
 * Handle delete confirmation
 */
export const handleDeleteConfirm = async (
  userToDelete,
  token,
  user,
  setFeedback,
  setDeleteDialogOpen,
  setUserToDelete,
  fetchUsersCallback
) => {
  try {
    await axiosInstance.delete(`/api/user/${userToDelete._id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    logger.info("User deleted successfully", {
      id: userToDelete._id,
      user: userToDelete.username,
      by: user?.username,
      role: user?.role,
    });
    setFeedback({
      open: true,
      message: "User deleted successfully",
      severity: "success",
    });
    setDeleteDialogOpen(false);
    setUserToDelete(null);
    fetchUsersCallback();
  } catch (err) {
    logger.error("Error deleting user", {
      error: err,
      by: user?.username,
      role: user?.role,
    });
    setFeedback({
      open: true,
      message: "Error deleting user",
      severity: "error",
    });
    setDeleteDialogOpen(false);
    setUserToDelete(null);
  }
};

/**
 * Handle delete cancel
 */
export const handleDeleteCancel = (user, setDeleteDialogOpen, setUserToDelete) => {
  logger.info("User Delete Confirmation closed without deleting", {
    by: user?.username,
    role: user?.role,
  });
  setDeleteDialogOpen(false);
  setUserToDelete(null);
};

/**
 * Handle view history
 */
export const handleViewHistory = (
  data,
  user,
  setHistoryModalOpen,
  setSelectedUserHistory,
  setFeedback
) => {
  if (
    data.updateHistory &&
    Array.isArray(data.updateHistory) &&
    data.updateHistory.length > 0
  ) {
    logger.info("View Updated history modal opened", {
      id: data._id,
      user: data.username,
      by: user?.username,
      role: user?.role,
    });
    setHistoryModalOpen(true);
    setSelectedUserHistory(data.updateHistory);
  } else {
    logger.warn("No history available", {
      id: data._id,
      user: data.username,
      by: user?.username,
      role: user?.role,
    });
    setFeedback({
      open: true,
      message: "No history available for this user",
      severity: "info",
    });
  }
};

/**
 * Handle history modal close
 */
export const handleHistoryClose = (
  user,
  setHistoryModalOpen,
  setSelectedUserHistory
) => {
  logger.info("Closed history modal", {
    by: user?.username,
    role: user?.role,
  });
  setHistoryModalOpen(false);
  setSelectedUserHistory([]);
};

/**
 * Handle add user button click
 */
export const handleAddUser = (user, setOpenUserPopup) => {
  logger.info("Add New User Modal opened in User list", {
    by: user?.username,
    role: user?.role,
  });
  setOpenUserPopup(true);
};

/**
 * Handle feedback close
 */
export const handleCloseFeedback = (feedback, setFeedback) => {
  setFeedback({ ...feedback, open: false });
};
